import { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { Badge } from '../ui/Badge';
import { formatCurrency } from '../../lib/formatCurrency';
import type { Order } from '@shared/types/order.types';
import type { License } from '@shared/types/order.types';

export function AccountDashboard() {
  const { user } = useAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [licenses, setLicenses] = useState<License[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetch('/api/orders', { credentials: 'include' }).then((r) => r.json()),
      fetch('/api/licenses', { credentials: 'include' }).then((r) => r.json()),
    ])
      .then(([ordersJson, licensesJson]) => {
        setOrders(ordersJson.data || []);
        setLicenses(licensesJson.data || []);
        setIsLoading(false);
      })
      .catch(() => setIsLoading(false));
  }, []);

  if (isLoading) {
    return <div className="animate-pulse space-y-4"><div className="h-20 rounded-lg bg-navy-800" /><div className="h-40 rounded-lg bg-navy-800" /></div>;
  }

  return (
    <div className="space-y-8">
      {/* User Info */}
      <div className="flex items-center gap-4 rounded-xl border border-white/10 bg-navy-900/50 p-6">
        {user?.avatarUrl && <img src={user.avatarUrl} alt="" className="h-16 w-16 rounded-full" />}
        <div>
          <h2 className="text-xl font-bold text-white">{user?.name || user?.email}</h2>
          <p className="text-sm text-gray-400">{user?.email}</p>
          <Badge variant="info" className="mt-1">{user?.provider}</Badge>
        </div>
      </div>

      {/* Orders */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-4">Orders</h3>
        {orders.length === 0 ? (
          <p className="text-gray-500">No orders yet</p>
        ) : (
          <div className="space-y-3">
            {orders.map((order) => (
              <div key={order.id} className="rounded-lg border border-white/10 bg-navy-900/30 p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">Order #{order.id.slice(-8)}</span>
                  <Badge variant={order.status === 'PAID' ? 'success' : order.status === 'REFUNDED' ? 'warning' : 'default'}>
                    {order.status}
                  </Badge>
                </div>
                <p className="mt-1 text-lg font-semibold text-green-400">{formatCurrency(order.totalAmount)}</p>
                {order.items && (
                  <div className="mt-2 space-y-1">
                    {order.items.map((item) => (
                      <p key={item.id} className="text-sm text-gray-400">
                        {item.product?.name} x{item.quantity}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Licenses */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-4">Licenses</h3>
        {licenses.length === 0 ? (
          <p className="text-gray-500">No licenses yet</p>
        ) : (
          <div className="space-y-3">
            {licenses.map((license) => (
              <div key={license.id} className="rounded-lg border border-white/10 bg-navy-900/30 p-4">
                <div className="flex items-center justify-between">
                  <code className="font-mono text-sm text-green-400">{license.key}</code>
                  <Badge variant={license.status === 'ACTIVE' ? 'success' : license.status === 'EXPIRED' ? 'warning' : 'error'}>
                    {license.status}
                  </Badge>
                </div>
                <p className="mt-1 text-sm text-gray-400">{license.product?.name}</p>
                <p className="text-xs text-gray-500">
                  Activations: {license.activationCount}/{license.maxActivations}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
